import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../auth/AuthContext";
import { hasRole } from "../../auth/hasRole";
import { TextContext } from "../../context/TextContext";
import {
    allUsersFromServer,
    createUserFromServer,
    deleteUserFromServer,
} from "./usersListService";

const UserslList = () => {
    const navigate = useNavigate();
    const { logoutUser } = useContext(AuthContext);
    const { setSharedText } = useContext(TextContext);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [users, setUsers] = useState([]);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", family: "", mobile: "", password: "" });
    const closeAddUserModalRef = useRef(null);
    const canManageUsers = hasRole("Manager");

    const getUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            setHasError(false);

            const data = await allUsersFromServer();
            const userList = Array.isArray(data) ? data : data?.users;

            if (!Array.isArray(userList)) {
                throw new Error("پاسخ سرور برای لیست کاربران معتبر نیست.");
            }

            setUsers(userList);
        } catch (err) {
            setHasError(true);
            toast.error(
                err.response?.data?.message || err.message || "خطا در دریافت لیست کاربران"
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        setSharedText("مدیریت کاربران");
        getUsers();
    }, [getUsers, setSharedText]);

    const getName = (user) =>
        `${user.name ?? ""} ${user.family ?? ""}`.trim() ||
        user.fullName ||
        user.userName ||
        "بدون نام";

    const getContact = (user) =>
        user.phoneNumber ?? user.email ?? user.userName ?? "—";

    const getRoles = (user) => {
        const roles = user.roles ?? user.role;
        const roleList = Array.isArray(roles)
            ? roles
            : typeof roles === "string"
                ? roles.split(",")
                : [];
        const roleLabels = {
            manager: "مدیر",
            user: "کاربر",
        };

        return roleList
            .map((role) => {
                if (typeof role !== "string") return role;
                const normalizedRole = role.trim().toLowerCase();
                return roleLabels[normalizedRole] ?? role.trim();
            })
            .filter(Boolean)
            .join("، ") || "—";
    };

    const hasManagerRole = (user) => {
        const roles = user.roles ?? user.role;
        const roleList = Array.isArray(roles)
            ? roles
            : typeof roles === "string"
                ? roles.split(",")
                : [];

        return roleList.some(
            (role) => typeof role === "string" && role.trim().toLowerCase() === "manager"
        );
    };

    const deleteUser = async (user) => {
        if (!canManageUsers || hasManagerRole(user)) return;

        const userId = user.id ?? user.userId;
        if (userId == null) {
            toast.error("شناسه کاربر برای حذف در دسترس نیست.");
            return;
        }

        const fullName = getName(user);
        if (!window.confirm(`آیا از حذف کاربر «${fullName}» مطمئن هستید؟`)) {
            return;
        }

        try {
            setDeletingUserId(userId);
            await deleteUserFromServer(userId);
            setUsers((currentUsers) =>
                currentUsers.filter((item) => (item.id ?? item.userId) !== userId)
            );
            toast.success("کاربر با موفقیت حذف شد.");
        } catch (err) {
            toast.error(
                err.response?.data?.message || "حذف کاربر با خطا مواجه شد."
            );
        } finally {
            setDeletingUserId(null);
        }
    };

    const createUser = async (event) => {
        event.preventDefault();
        if (!canManageUsers || isCreatingUser) return;

        try {
            setIsCreatingUser(true);
            await createUserFromServer(
                newUser.name.trim(),
                newUser.family.trim(),
                newUser.mobile.trim(),
                newUser.password
            );
            setNewUser({ name: "", family: "", mobile: "", password: "" });
            closeAddUserModalRef.current?.click();
            toast.success("کاربر با موفقیت اضافه شد.");
            await getUsers();
        } catch (err) {
            toast.error(err.response?.data?.message || "افزودن کاربر با خطا مواجه شد.");
        } finally {
            setIsCreatingUser(false);
        }
    };

    return (
        <div className="users-list-page" dir="rtl">
            <header className="users-list-header">
                <div className="header-info">
                    <div className="header-icon">
                        <img src="https://absence.samradco.ir/favicon.ico" className="img-fluid" alt="" />
                    </div>
                    <div>
                        <h1>حضور و غیاب سامراد</h1>
                        <p>مدیریت کاربران</p>
                    </div>
                </div>
                {canManageUsers && (
                    <button
                        type="button"
                        className="users-list-add-button"
                        data-bs-toggle="modal"
                        data-bs-target="#AddUserModal"
                    >
                        <span aria-hidden="true">+</span>
                        افزودن کاربر
                    </button>
                )}
            </header>

            <main className="container-fluid3">
                {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">در حال بارگذاری...</span>
                        </div>
                    </div>
                )}

                {!isLoading && hasError && (
                    <div className="text-center py-5" role="alert">
                        <p>دریافت لیست کاربران با خطا مواجه شد.</p>
                        <button type="button" className="btn btn-outline-primary" onClick={getUsers}>
                            تلاش مجدد
                        </button>
                    </div>
                )}

                {!isLoading && !hasError && (
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4 py-3">نام</th>
                                        <th className="py-3">موبایل</th>
                                        <th className="py-3">نقش</th>
                                        {canManageUsers && <th className="py-3 text-center">عملیات</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user, index) => (
                                        <tr key={user.id ?? user.userId ?? index}>
                                            <td className="px-4">{getName(user)}</td>
                                            <td>{getContact(user)}</td>
                                            <td>{getRoles(user)}</td>
                                            {canManageUsers && !hasManagerRole(user) && (
                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => deleteUser(user)}
                                                        disabled={deletingUserId === (user.id ?? user.userId)}
                                                        aria-label={`حذف ${getName(user)}`}
                                                    >
                                                        {deletingUserId === (user.id ?? user.userId)
                                                            ? "در حال حذف..."
                                                            : "حذف"}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {users.length === 0 && (
                                        <tr>
                                            <td colSpan={canManageUsers ? 4 : 3} className="text-center py-5 text-muted">
                                                کاربری یافت نشد.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>
            {canManageUsers && (
                <div
                    className="modal fade"
                    id="AddUserModal"
                    tabIndex="-1"
                    aria-labelledby="AddUserModalLabel"
                    aria-hidden="true"
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <form className="modal-content" onSubmit={createUser}>
                            <div className="modal-header">
                                <h2 className="modal-title fs-5" id="AddUserModalLabel">
                                    افزودن کاربر
                                </h2>
                                <button
                                    type="button"
                                    className="btn-close ms-0 me-auto"
                                    data-bs-dismiss="modal"
                                    aria-label="بستن"
                                    disabled={isCreatingUser}
                                />
                            </div>
                            <div className="modal-body">
                                <label htmlFor="new-user-name" className="form-label">نام</label>
                                <input
                                    id="new-user-name"
                                    className="form-control mb-3"
                                    type="text"
                                    autoComplete="given-name"
                                    value={newUser.name}
                                    onChange={(event) =>
                                        setNewUser((current) => ({ ...current, name: event.target.value }))
                                    }
                                    required
                                />

                                <label htmlFor="new-user-family" className="form-label">نام خانوادگی</label>
                                <input
                                    id="new-user-family"
                                    className="form-control mb-3"
                                    type="text"
                                    autoComplete="family-name"
                                    value={newUser.family}
                                    onChange={(event) =>
                                        setNewUser((current) => ({ ...current, family: event.target.value }))
                                    }
                                    required
                                />

                                <label htmlFor="new-user-mobile" className="form-label">موبایل</label>
                                <input
                                    id="new-user-mobile"
                                    className="form-control"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={newUser.mobile}
                                    onChange={(event) =>
                                        setNewUser((current) => ({ ...current, mobile: event.target.value }))
                                    }
                                    required
                                />

                                <label htmlFor="new-user-password" className="form-label mt-3">رمز عبور</label>
                                <input
                                    id="new-user-password"
                                    dir="ltr"
                                    className="form-control"
                                    type="password"
                                    autoComplete="new-password"
                                    value={newUser.password}
                                    onChange={(event) =>
                                        setNewUser((current) => ({ ...current, password: event.target.value }))
                                    }
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={isCreatingUser}
                                >
                                    انصراف
                                </button>
                                <button
                                    ref={closeAddUserModalRef}
                                    type="button"
                                    className="d-none"
                                    data-bs-dismiss="modal"
                                    tabIndex="-1"
                                    aria-hidden="true"
                                />
                                <button type="submit" className="btn btn-primary" disabled={isCreatingUser}>
                                    {isCreatingUser ? "در حال ذخیره..." : "ذخیره کاربر"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <style>{`
                .users-list-header {
                    min-height: 68px;
                    padding: 8px 14px;
                    background: #726be7;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                }

                .users-list-header .header-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .users-list-header .header-icon {
                    width: 50px;
                    height: 50px;
                    flex-shrink: 0;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .users-list-header h1 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 800;
                }

                .users-list-header p {
                    margin: 3px 0 0;
                    font-size: 13px;
                }

                .users-list-add-button {
                    min-height: 40px;
                    padding: 0 14px;
                    border: 1px solid rgba(255, 255, 255, .36);
                    border-radius: 11px;
                    background: rgba(255, 255, 255, .16);
                    color: #fff;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    font: inherit;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                    cursor: pointer;
                }

                .users-list-add-button:hover {
                    background: rgba(255, 255, 255, .26);
                }

                .users-list-add-button span {
                    font-size: 20px;
                    line-height: 1;
                }

                .users-list-absences-button {
                    background: rgba(255,255,255,0.15);
                    color: #fff;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 8px;
                    margin-left: 10px;
                    padding: 7px 16px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                }

                @media (max-width: 360px) {
                    .users-list-header {
                        padding: 0 10px;
                    }

                    .users-list-add-button {
                        min-height: 38px;
                        padding: 0 10px;
                        font-size: 11px;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserslList;
